import {InputAdornment} from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

import TextField from "./TextField.js";

import scss from "./QuantityInput.module.scss";

/**
 * Price input.
 *
 * @package HOPS
 * @subpackage Components
 * @author Carl Booth
 * @copyright Heritage Operations Processing Limited
 */
class QuantityInput extends React.PureComponent {

	/**
	 * Constructor.
	 *
	 * @param {Object} props
	 * @return {self}
	 */
	constructor(props) {
		super(props);

		/**
		 * State
		 *
		 * @type {Object}
		 */
		this.state = {

			/**
			 * Focused?
			 * 
			 * @type {Boolean}
			 */
			focused: false,

			/**
			 * Initial value
			 *
			 * @type {String|null}
			 */
			value: props.value,

			maxValue: props.maxValue,

			minValue: props.minValue
		};

		/**
		 * Method binds
		 */
		this.handleBlur = this.handleBlur.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.handleFocus = this.handleFocus.bind(this);

	}


	/**
	 * Component updated.
	 *
	 * @param {Object} prevProps
	 * @return {void}
	 */
	componentDidUpdate(prevProps) {
		if (prevProps.value !== this.props.value) {
			this.setState({value: this.props.value});
		}
	}


	/**
	 * Blurred.
	 *
	 * @return {void}
	 */
	handleBlur() {

		this.setState({focused: false});

		if (this.state.value) {
			const value = this.valueFormatted;
			this.reportChange(value, this.props.name);
			this.setState({value});
		}

		if (this.props.onBlur) this.props.onBlur();

	}


	/**
	 * Value changed.
	 * 
	 * @param {String} value
	 * @return {void}
	 */
	handleChange(value, name) {
		this.reportChange(value, name);
		this.setState({value});
	}


	/**
	 * Focused.
	 * 
	 * @return {void}
	 */
	handleFocus() {
		this.setState({focused: true});
	}


	/**
	 * Report a value change.
	 * 
	 * @param {mixed} value
	 * @return {void}
	 */
	reportChange(value, name) {
		const parsed = Number.parseFloat(value);
		this.props.onChange(parsed, name);
	}


	/**
	 * Render.
	 * 
	 * @return {ReactNode}
	 */
	render() {
		return (
			<TextField
				autoFocus={this.props.autoFocus}
				color={this.props.color}
				controlled={true}
				disabled={this.props.disabled}
				fullWidth={this.props.fullWidth}
				inputClassname={scss.inputClassname}
				inputProps={this.constructor.inputProps}
				InputProps={this.constructor.InputProps}
				label={this.props.label}
				name={this.props.name}
				onBlur={this.handleBlur}
				onChange={this.handleChange}
				onFocus={this.handleFocus}
				required={this.props.required}
				pattern="^[0-9]+$" /* Must be a whole number */
				placeholder={this.props.placeholder}
				value={this.value} />
		);
	}


	/**
	 * Get `value`.
	 * 
	 * @return {mixed}
	 */
	get value() {
		if (this.state.value === null) return null;
		else if (this.props.value === null || this.state.focused) return Math.max(this.state.value, 0);
		else return this.valueFormatted;
	}


	/**
	 * Get `value` bound in the max/min range. Mui has a bug with type="number" that doesn't catch this
	 * 
	 * @return {String|null}
	 */
	get valueFormatted() {

		/* Bound value to acceptable range */
		let boundValue = parseInt(this.state.value);
		if (boundValue < this.props.minValue) boundValue = this.props.minValue;
		else if (boundValue > this.props.maxValue) boundValue = this.props.maxValue;

		return boundValue;
	}


	/**
	 * `inputProps`
	 * 
	 * @type {Object}
	 */
	static inputProps = {};

	/**
	 * `InputProps`
	 * 
	 * @type {Object}
	 */
	get InputProps() {
		return {startAdornment: this.props.withStartAdornment ? <InputAdornment position="start">Qty</InputAdornment> : undefined};
	}

	static propTypes = {
		withStartAdornment: PropTypes.bool
	};

	static defaultProps = {
		withStartAdornment: true
	};

}

export default QuantityInput;
